/**
 * Authenticated fetch for the auth service's custom Hono routes.
 *
 * Better Auth client methods carry the session for us; our own routes
 * (`/api/auth/username/claim`, `/api/auth/password/set`) are plain `fetch`
 * calls and must do it themselves. Two mechanisms are needed:
 *
 *   - web: `credentials: 'include'` sends the browser's session cookie.
 *   - native: there is no cookie jar. The Expo client keeps the session token
 *     in AsyncStorage and exposes `authClient.getCookie()` for exactly this
 *     case, so we set the `Cookie` header by hand.
 *
 * Both are always applied: the header is simply absent on web (where
 * `getCookie` returns an empty string), and `credentials` is inert on native.
 */

import { authClient } from '@/config/auth-client';
import { ENV } from '@/config/env';

const DEFAULT_METHOD = 'GET';
const JSON_CONTENT_TYPE = 'application/json';

export interface AuthFetchOptions {
  /** HTTP method. Defaults to GET. */
  method?: string;
  /** Serialised to JSON and sent with a JSON content type when present. */
  body?: unknown;
  /** Extra headers. The session cookie and content type are added on top. */
  headers?: Readonly<Record<string, string>>;
}

/** A client that may or may not expose the Expo plugin's `getCookie` action. */
type CookieCapableClient = { getCookie?: () => string };

/**
 * The Expo client's stored session cookie, or an empty string when there is
 * none (web builds, or a client without the Expo plugin). Never throws: a
 * storage failure must degrade to "no header", not break the request.
 */
const getSessionCookie = (): string => {
  try {
    const cookie = (authClient as CookieCapableClient).getCookie?.();
    return typeof cookie === 'string' ? cookie : '';
  } catch {
    return '';
  }
};

const buildHeaders = (
  options: AuthFetchOptions
): Readonly<Record<string, string>> => {
  const cookie = getSessionCookie();
  return {
    ...(options.headers ?? {}),
    ...(options.body === undefined ? {} : { 'Content-Type': JSON_CONTENT_TYPE }),
    ...(cookie ? { Cookie: cookie } : {}),
  };
};

/**
 * Perform an authenticated request against the auth service.
 *
 * Rejects on network failure exactly as `fetch` does; callers own the retry or
 * fallback policy. Non-2xx responses resolve normally so callers can branch on
 * `response.status` rather than on message text.
 */
export async function authFetch(
  path: string,
  options: AuthFetchOptions = {}
): Promise<Response> {
  return fetch(`${ENV.AUTH_URL}${path}`, {
    method: options.method ?? DEFAULT_METHOD,
    credentials: 'include',
    headers: buildHeaders(options),
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });
}

/**
 * Parse a response body as JSON, falling back to an empty object.
 *
 * Error responses from the auth service are not guaranteed to carry a JSON
 * body (a proxy or an unhandled throw may return HTML), and a parse failure
 * must never mask the status code the caller is branching on.
 */
export async function safeJson(response: Response): Promise<Record<string, unknown>> {
  try {
    return (await response.json()) as Record<string, unknown>;
  } catch {
    return {};
  }
}
